// Self-contained HTML for the DocRaptor (PrinceXML) PDF download. The site's
// Tailwind v4 CSS doesn't survive Prince (cascade layers, oklch()), so the PDF
// gets its own plain CSS here: hex colours, tables for layout, CSS Paged Media
// footers. Mirrors ReportDocument's section order — keep the two in step when
// sections change.
import { riskScore, riskBand, RISK_BAND_LABEL } from "./templateContent";
import { splitEmergency, type RamsReport, type RiskPair } from "./reports";
import { TOLERABILITY, MATRIX_SEVERITIES, MATRIX_LIKELIHOODS, BAND_TINT, groupCitations, formatReviewDate, alarpStatement } from "../components/ReportDocument";
import { DISCLAIMER } from "./site";
import { brandColor, safeLogo, headerTheme, type Brand } from "./brand";

const esc = (s: unknown): string =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const BAND_COLOR: Record<string, string> = {
  low: "#16a34a",
  medium: "#eab308",
  high: "#ea580c",
  "very-high": "#dc2626",
};

function chip(r: RiskPair): string {
  const score = riskScore(r);
  const band = riskBand(score);
  const dark = band === "medium";
  return `<span class="chip" style="background:${BAND_COLOR[band]};color:${dark ? "#1f2a44" : "#fff"}" title="L${r.likelihood}×S${r.severity}">${score}</span>`;
}

const LOGO = `<svg viewBox="0 0 40 40" width="22" height="22" aria-hidden="true" fill="none"><rect x="12" y="5" width="20" height="27" rx="3" fill="#cdd6e6"/><rect x="7" y="8.5" width="20" height="27" rx="3" fill="#16204a"/><rect x="11" y="15" width="12" height="2" rx="1" fill="#fff" opacity="0.92"/><rect x="11" y="20" width="12" height="2" rx="1" fill="#fff" opacity="0.55"/><rect x="11" y="25" width="8" height="2" rx="1" fill="#fff" opacity="0.55"/><circle cx="29.5" cy="28.5" r="8.5" fill="#1ba37b" stroke="#fff" stroke-width="2"/><path d="M26 28.5 l2.4 2.4 l4.6 -5" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const CSS = `
@page {
  size: A4;
  margin: 16mm 14mm 18mm;
  @bottom-left { content: string(doctitle); font: 7.5pt "Helvetica Neue", Arial, sans-serif; color: #94a3b8; }
  @bottom-right { content: "Page " counter(page) " of " counter(pages); font: 7.5pt "Helvetica Neue", Arial, sans-serif; color: #94a3b8; }
}
* { box-sizing: border-box; }
body { font: 9.5pt/1.55 "Helvetica Neue", Arial, sans-serif; color: #334155; margin: 0; }
h1 { string-set: doctitle content(); }
.band { background: #0b0c2a; color: #fff; padding: 16pt 18pt; }
.band .brand { font-size: 10pt; font-weight: 700; }
.band .brand svg { vertical-align: -5pt; margin-right: 4pt; }
.band h1 { font-size: 16pt; line-height: 1.25; margin: 10pt 0 3pt; color: #fff; }
.band .meta { font-size: 8pt; color: rgba(255,255,255,0.7); margin: 0; }
main { padding: 14pt 2pt 0; }
section { margin-bottom: 14pt; }
h2 { font-size: 11pt; color: #0f172a; border-bottom: 1.6pt solid #1ba37b; padding-bottom: 3pt; margin: 0 0 7pt; }
h3 { font-size: 9.5pt; color: #0f172a; margin: 0 0 3pt; }
p { margin: 0 0 5pt; }
ul, ol { margin: 0 0 5pt; padding-left: 14pt; }
li { margin-bottom: 2.5pt; }
table { width: 100%; border-collapse: collapse; }
.fields td { width: 33.33%; padding: 4pt 8pt 4pt 0; vertical-align: top; }
.fl { font-size: 6.5pt; letter-spacing: 0.07em; text-transform: uppercase; color: #64748b; font-weight: 700; }
.fv { color: #0f172a; font-weight: 600; white-space: pre-line; }
.fe { color: #94a3b8; font-style: italic; }
.req { color: #ea580c; }
.risk th { background: #f1f5f9; font-size: 6.8pt; letter-spacing: 0.07em; text-transform: uppercase; color: #64748b; text-align: left; padding: 4pt 7pt; border: 0.75pt solid #e2e8f0; }
.risk td { padding: 4pt 7pt; border: 0.75pt solid #e2e8f0; }
.risk .c { text-align: center; width: 52pt; }
.chip { display: inline-block; min-width: 18pt; padding: 1.5pt 4pt; border-radius: 4pt; font-weight: 700; font-size: 8pt; text-align: center; }
.note { font-size: 7.5pt; color: #64748b; margin-top: 4pt; }
.matrix { page-break-inside: avoid; }
.matrix th, .matrix td { border: 0.75pt solid #e2e8f0; padding: 3.5pt 4pt; text-align: center; font-size: 6.8pt; }
.matrix thead th { background: #f1f5f9; color: #0f172a; font-weight: 700; }
.matrix th.corner { background: #f1f5f9; font-size: 6pt; letter-spacing: 0.06em; text-transform: uppercase; color: #64748b; text-align: left; }
.matrix th.rl { background: #fff; color: #0f172a; text-align: right; font-weight: 700; }
.matrix td { font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
.tol { page-break-inside: avoid; margin-top: 6pt; }
.tol td { width: 25%; border: 0.75pt solid #e2e8f0; border-radius: 3pt; padding: 5pt 7pt; vertical-align: top; }
.tol strong { display: block; font-size: 8pt; color: #0f172a; margin-bottom: 1.5pt; }
.tol span { font-size: 7.5pt; color: #64748b; line-height: 1.4; }
.haz { border: 0.75pt solid #e2e8f0; border-radius: 4pt; padding: 8pt 10pt; margin-bottom: 7pt; page-break-inside: avoid; }
.haz .who { font-size: 8pt; color: #64748b; margin: 2pt 0 4pt; }
.haz .basis { font-size: 7.5pt; color: #64748b; font-style: italic; margin: 4pt 0 0; }
.haz .chips { float: right; font-size: 7.5pt; color: #64748b; }
.jobnotes { background: #f1f5f9; border-radius: 4pt; padding: 7pt 10pt; margin-top: 6pt; page-break-inside: avoid; }
.jobnotes .fl { margin-bottom: 3pt; }
.seq td.n { width: 16pt; vertical-align: top; }
.seq td { padding: 0 0 5pt; }
.num { display: inline-block; width: 12pt; height: 12pt; border-radius: 6pt; background: #d8f3ea; color: #0e7a5c; font-size: 7.5pt; font-weight: 700; text-align: center; line-height: 12pt; }
.ppe td { width: 50%; padding: 1.5pt 8pt 1.5pt 0; }
.dot { display: inline-block; width: 4pt; height: 4pt; border-radius: 2pt; background: #1ba37b; vertical-align: 2pt; margin-right: 4pt; }
.sign td { width: 33.33%; padding: 4pt 8pt 4pt 0; vertical-align: top; }
.sigline { color: #64748b; font-size: 7.5pt; margin-top: 8pt; }
.ops th { background: #f1f5f9; font-size: 6.8pt; letter-spacing: 0.07em; text-transform: uppercase; color: #64748b; text-align: left; padding: 4pt 7pt; border: 0.75pt solid #e2e8f0; }
.ops td { border: 0.75pt solid #e2e8f0; height: 18pt; }
.disclaimer { border-top: 0.75pt solid #e2e8f0; padding-top: 7pt; font-size: 7.5pt; color: #64748b; }
.siteplan { border: 0.75pt solid #e2e8f0; border-radius: 4pt; overflow: hidden; }
.siteplan svg { width: 100%; height: auto; display: block; }
.avoid { page-break-inside: avoid; }
/* Page-1 watermark layer. position:fixed does NOT repeat across pages in the
   DocRaptor render (verified 2026-06-06) — drafts also get @prince-overlay,
   injected per-document below, so every page is marked. */
.wm { position: fixed; top: 42%; left: -5%; width: 110%; text-align: center; transform: rotate(-22deg); font-size: 30pt; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: rgba(220, 38, 38, 0.12); z-index: 10; }
.draftbox { border: 1.2pt solid #dc2626; background: #fef2f2; border-radius: 4pt; padding: 7pt 10pt; margin: 12pt 0 0; color: #b3261e; font-size: 8pt; page-break-inside: avoid; }
.draftbox strong { display: block; font-size: 8.5pt; margin-bottom: 2pt; }
.draftbox ul { margin: 2pt 0 0; padding-left: 12pt; }
`;

export function buildPdfHtml(opts: {
  report: RamsReport;
  values: Record<string, string>;
  enabled: Set<string>;
  permits: string[];
  scopeLines: string[];
  /** Present when readiness < 100% — watermarks every page and lists what's outstanding. */
  draft?: { readiness: number; missing: string[] };
  /** ISO issue date stamped in the header (the download moment). */
  issueDate?: string;
  /** Serialised site-plan SVG (taken from the live preview) with its scale caption. */
  sitePlan?: { svg: string; caption: string };
  /** Optional document branding — the user's logo and accent colour. */
  brand?: Brand;
}): string {
  const { report, values, enabled, permits, scopeLines, draft, issueDate, sitePlan } = opts;

  // Optional branding: swap the RamsDocs mark for the user's logo, recolour the
  // header band + section underlines, with header text auto-contrasted so a light
  // header (to match a white-background logo) stays readable. Falls back to defaults.
  const accent = brandColor(opts.brand?.color);
  const headerColor = brandColor(opts.brand?.headerColor);
  const ht = headerTheme(headerColor);
  const logo = safeLogo(opts.brand?.logo);
  const brandMark = logo
    ? `<img src="${logo}" alt="" style="height:26pt;width:auto;max-width:170pt;vertical-align:middle"/>`
    : `${LOGO}RamsDocs`;
  const bandRules = [
    ...(headerColor ? [`background:${ht.bg}`, `color:${ht.fg}`] : []),
    ...(accent ? [`border-top:4pt solid ${accent}`] : []),
  ];
  const brandCss = [
    accent ? `h2{border-bottom-color:${accent};}` : "",
    bandRules.length ? `.band{${bandRules.join(";")};}` : "",
    headerColor ? `.band .brand{color:${ht.fg};}.band h1{color:${ht.fg};}.band .meta{color:${ht.muted};}` : "",
  ].join("");
  const hazards = report.hazards.filter((h) => enabled.has(h.hazardId));
  const val = (key: string) => (values[key] || "").trim();

  // Optional fields appear only once filled; required ones always show.
  const fieldCells = report.siteFields.filter((f) => f.required || val(f.key)).map((f) => {
    const v = val(f.key);
    return `<div class="fl">${esc(f.label)}${f.required ? '<span class="req"> *</span>' : ""}</div>${
      v ? `<div class="fv">${esc(v)}</div>` : `<div class="fe">To be completed</div>`
    }`;
  });
  const fieldRows: string[] = [];
  for (let i = 0; i < fieldCells.length; i += 3) {
    fieldRows.push(`<tr>${fieldCells.slice(i, i + 3).map((c) => `<td>${c}</td>`).join("")}</tr>`);
  }

  const riskRows = hazards
    .map((h) => `<tr><td>${esc(h.hazard)}</td><td class="c">${chip(h.initialRisk)}</td><td class="c">${chip(h.residualRisk)}</td></tr>`)
    .join("");

  const hazardBlocks = hazards
    .map((h) => {
      const i = riskScore(h.initialRisk);
      const r = riskScore(h.residualRisk);
      return `<div class="haz">
        <span class="chips">${chip(h.initialRisk)} ${esc(RISK_BAND_LABEL[riskBand(i)])} &nbsp;&rarr;&nbsp; ${chip(h.residualRisk)} ${esc(RISK_BAND_LABEL[riskBand(r)])}</span>
        <h3>${esc(h.hazard)}</h3>
        <p class="who"><strong>Who is at risk:</strong> ${esc(h.whoIsAtRisk.join(", "))}</p>
        <ul>${h.controls.map((c) => `<li>${esc(c)}</li>`).join("")}</ul>
        ${h.citation ? `<p class="basis">Basis: ${esc(h.citation)}</p>` : ""}
      </div>`;
    })
    .join("");

  // Renumber positionally — trigger answers can drop steps, leaving gaps in `order`.
  // Where permits apply, a standing hold-point step leads the sequence so the
  // permit is a briefed gate in the method, not just a list entry below.
  const seqSteps = [
    ...(permits.length
      ? [{ description: `HOLD POINT — before any permit-controlled operation starts: obtain the required permit(s) (${permits.join("; ")}) from the issuer named in this document, display the live permit at the workface and brief its conditions to the operatives. Work covered by a permit does not begin or resume without a live permit.` }]
      : []),
    ...report.sequenceOfWorks,
  ];
  const seqRows = seqSteps
    .map((s, i) => `<tr class="avoid"><td class="n"><span class="num">${i + 1}</span></td><td>${esc(s.description)}</td></tr>`)
    .join("");

  const ppeRows: string[] = [];
  for (let i = 0; i < report.ppe.length; i += 2) {
    ppeRows.push(
      `<tr>${report.ppe.slice(i, i + 2).map((p) => `<td><span class="dot"></span>${esc(p)}</td>`).join("")}</tr>`,
    );
  }

  const legislation = groupCitations(report, hazards);
  const alarp = alarpStatement(hazards);

  // Site-specific emergency information — generic emergency text without a
  // named first aider and assembly point is a standard rejection trigger.
  const emergencyCells = [
    { label: "Emergency services", value: "999 — Fire / Police / Ambulance", required: true },
    { label: "Address to give 999", value: val("siteAddress").replace(/\n+/g, ", "), required: true },
    { label: "First aider", value: val("firstAider"), required: true },
    { label: "Assembly point", value: val("assemblyPoint"), required: true },
    { label: "Nearest A&E", value: val("nearestAE"), required: false },
  ]
    .filter((i) => i.required || i.value)
    .map((i) => `<div class="fl">${esc(i.label)}</div>${i.value ? `<div class="fv">${esc(i.value)}</div>` : `<div class="fe">To be completed</div>`}`);
  const emergencyRows: string[] = [];
  for (let i = 0; i < emergencyCells.length; i += 3) {
    emergencyRows.push(`<tr>${emergencyCells.slice(i, i + 3).map((c) => `<td>${c}</td>`).join("")}</tr>`);
  }

  const emergency = splitEmergency(report.emergencyRescue)
    .map((s) => `<p class="avoid">${s.heading ? `<strong>${esc(s.heading)}:</strong> ` : ""}${esc(s.body)}</p>`)
    .join("");

  // Names populate from Site details — no write-in line, just the signature.
  const signBox = (label: string, v: string) =>
    `<td><div class="fl">${esc(label)}</div>${v ? `<div class="fv">${esc(v)}</div>` : `<div class="fe">To be completed</div>`}<div class="sigline">Signature: ______________________</div></td>`;

  const missing = (draft?.missing || []).slice(0, 8);
  const draftBox = draft
    ? `<div class="draftbox"><strong>Incomplete — not for submission (${draft.readiness}% ready)</strong>Outstanding before this RAMS is submission-ready:<ul>${missing.map((m) => `<li>${esc(m)}</li>`).join("")}</ul></div>`
    : "";
  // Prince's per-page overlay region — the .wm fixed layer only reaches page 1.
  const draftOverlayCss = draft
    ? `@page { @prince-overlay { content: "Incomplete — not for submission"; font: 800 26pt "Helvetica Neue", Arial, sans-serif; letter-spacing: 0.12em; text-transform: uppercase; color: rgba(220, 38, 38, 0.10); transform: rotate(-22deg); } }`
    : "";

  return `<!doctype html>
<html><head><meta charset="utf-8"><style>${CSS}${draftOverlayCss}${brandCss}</style></head><body>
${draft ? `<div class="wm">Incomplete — not for submission</div>` : ""}
<div class="band">
  <span class="brand">${brandMark}</span>
  <h1>${esc(report.title)}</h1>
  <p class="meta">${esc(report.trade)} · ${esc(report.task)} · Document ref ${esc(report.id.toUpperCase())}-V${report.version} · Rev ${esc(val("revision") || "A")}${issueDate ? ` · Issued ${esc(formatReviewDate(issueDate))}` : ""}${report.lastReviewed ? ` · Library reviewed ${esc(formatReviewDate(report.lastReviewed))}` : ""}</p>
</div>
<main>
${draftBox}
<section><h2>Project control details</h2><table class="fields">${fieldRows.join("")}</table></section>

<section><h2>Scope of works</h2>
<p>${esc(report.scopeOfWorks)}</p>
${val("workLocationOnSite") ? `<p><strong>Work location:</strong> ${esc(val("workLocationOnSite"))}${val("siteAddress") ? `, ${esc(val("siteAddress").replace(/\n+/g, ", "))}` : ""}</p>` : ""}
${scopeLines.length ? `<div class="jobnotes"><div class="fl">Job-specific arrangements</div><ul>${scopeLines.map((l) => `<li>${esc(l)}</li>`).join("")}</ul></div>` : ""}
</section>

<section class="avoid"><h2>Risk rating matrix &amp; tolerability</h2>
<table class="matrix"><thead><tr><th class="corner">Likelihood &darr; &middot; Severity &rarr;</th>${MATRIX_SEVERITIES.map((s) => `<th>${esc(s)}</th>`).join("")}</tr></thead>
<tbody>${MATRIX_LIKELIHOODS.map(([label, l]) =>
  `<tr><th class="rl">${esc(label)}</th>${MATRIX_SEVERITIES.map((_, i) => {
    const band = riskBand(l * (i + 1));
    const tint = BAND_TINT[band];
    return `<td style="background:${tint.bg};color:${tint.text}">${esc(RISK_BAND_LABEL[band])}</td>`;
  }).join("")}</tr>`
).join("")}</tbody></table>
<table class="tol"><tr>${TOLERABILITY.map((t) => `<td><strong>${esc(t.band)}</strong><span>${esc(t.action)}</span></td>`).join("")}</tr></table>
</section>

<section><h2>Risk assessment</h2>
<table class="risk"><tr><th>Hazard</th><th style="text-align:center">Initial</th><th style="text-align:center">Residual</th></tr>${riskRows || `<tr><td colspan="3" class="fe">No hazards in scope.</td></tr>`}</table>
<p class="note">Risk score = likelihood (1–5) × severity (1–5). Bands: 1–4 low · 5–9 medium · 10–15 high · 16–25 very high. Residual assumes the stated controls are in place.</p>
${alarp ? `<div class="jobnotes"><div class="fl">ALARP justification</div><p>${esc(alarp)}</p></div>` : ""}
</section>

<section><h2>Hazards &amp; control measures</h2>${hazardBlocks}</section>

<section><h2>Method statement — sequence of works</h2><table class="seq">${seqRows}</table></section>

<section class="avoid"><h2>Personal protective equipment</h2><table class="ppe">${ppeRows.join("")}</table></section>

<section class="avoid"><h2>Permits, substances &amp; competence</h2>
${permits.length
  ? `<p><strong>Permits required:</strong> ${esc(permits.join("; "))}${val("permitsRequired") ? ` — ${esc(val("permitsRequired"))}` : ""}</p>`
  : val("permitsRequired")
    ? `<p><strong>Permits:</strong> ${esc(val("permitsRequired"))}</p>`
    : `<p><strong>Permits required:</strong> None identified for this task by this assessment, and none are triggered by the planned method. If the principal contractor's site rules require a permit for any activity here, that permit is obtained before the activity starts.</p>`}
${report.substances.length
  ? `<p><strong>Substances (COSHH):</strong> ${esc(report.substances.join("; "))}</p>
${val("substanceProducts")
  ? `<p class="note"><strong>Products in use:</strong> ${esc(val("substanceProducts"))}. The SDS for each named product is held with this RAMS in the site file and was used to set the controls above.</p>`
  : `<p class="note">Safety data sheets for the named products are held with this RAMS in the site file and were used to set the controls above; product names and SDS dates are recorded in the briefing record.</p>`}`
  : `<p><strong>Substances (COSHH):</strong> No hazardous substances are used or generated by this task as planned. If any substance is introduced (adhesives, sealants, fuels, significant dust), work on the affected activity stops until a COSHH assessment for that substance is completed and briefed.</p>`}
${report.competence.length ? `<ul>${report.competence.map((c) => `<li>${esc(c.requirement)}</li>`).join("")}</ul>` : ""}
${val("operatives") ? `<p><strong>Operative competence record:</strong> ${esc(val("operatives"))}</p>` : ""}
</section>

<section><h2>Emergency &amp; rescue arrangements</h2>
<table class="fields avoid" style="margin-bottom:6pt">${emergencyRows.join("")}</table>
${emergency}</section>

<section class="avoid"><h2>Legislation &amp; guidance</h2><ul>${legislation.map((l) => `<li>${esc(l)}</li>`).join("")}</ul></section>

${
  sitePlan
    ? `<section class="avoid"><h2>Site plan sketch</h2><div class="siteplan">${sitePlan.svg}</div><p class="note">${esc(sitePlan.caption)}</p></section>`
    : ""
}
<section class="avoid"><h2>Briefing &amp; sign-off</h2>
<table class="sign"><tr>${signBox("Prepared by", val("preparedBy"))}${signBox("Site supervisor / competent person", val("competentPerson"))}${signBox("Review date", val("reviewDate"))}</tr></table>
<p class="note">All operatives must be briefed on this RAMS before work starts and sign below to confirm they understand it. Anyone joining the task later is briefed and signs before starting work; the supervisor re-briefs the team if the method, conditions or this document change.</p>
<table class="ops"><tr><th style="width:40%">Name</th><th style="width:40%">Signature</th><th>Date</th></tr>${
  // Pre-populate rows with the named operatives (split on ";"), then blank
  // rows for joiners — a fully blank table reads as a recycled template.
  (val("operatives") ? val("operatives").split(";").map((o) => `<tr><td>${esc(o.split("—")[0].split("(")[0].trim())}</td><td></td><td></td></tr>`).join("") : "")
}${"<tr><td></td><td></td><td></td></tr>".repeat(val("operatives") ? 2 : 5)}</table>
</section>

<p class="disclaimer">${esc(DISCLAIMER)}</p>
</main>
</body></html>`;
}
